import { toObj, up } from "util/ext";
import { sortMedia } from "util/sorts";
import * as applicationActionTypes from "application/redux/applicationActionTypes";
import * as actionTypes from "browse-media/redux/browseMediaActionTypes";
import {
  append,
  clone,
  find,
  findIndex,
  flip,
  isEmpty,
  isNil,
  pipe,
  propEq,
  reject,
  map,
  union,
  without,
} from "ramda";

import {
  filterYearTermsInGroup,
  findGroupForYearTerm,
  findMediaItems,
  getIntersectionOfParentModuleIds,
  getPublicationYearGroups,
  getUnionOfParentModuleIds,
  mergeFacetCollections,
  normalizeFacetResults,
  rejectYearTermsInGroup,
  sortPublicationYearGroups,
  unionPublicationYearGroups,
  unionYearTerms,
  updateFacetTermCounts,
  withoutGroup,
  withoutYearTerm,
} from "browse-media/redux/browseMediaHelpers";

const DRAWER_ITEM_LIMIT = 100;

const initialState = {
  addModulesDrawer: {
    filterQuery: "",
    itemCount: 0,
    itemLimit: DRAWER_ITEM_LIMIT,
    modules: [],
    modulesLoaded: false,
    selectedModuleIds: [],
    visible: false,
    visibleModules: [],
  },
  appliedFacets: [],
  appliedQuery: "",
  availableFacets: {},
  appliedPublicationYearGroups: [],
  availablePublicationYearGroups: [],
  facetDialog: {
    availableTerms: [],
    displayName: "",
    expandedPublicationYearGroups: [],
    name: "",
    selectedPublicationYearGroups: [],
    selectedTerms: [],
    visible: false,
  },
  limit: 50,
  mediaList: [],
  mediaLoaded: false,
  offset: 0,
  query: "",
  removeModulesDrawer: {
    mediaItems: [],
    modules: [],
    selectedModuleIds: [],
    visible: false,
    modulesLoaded: false,
    confirmationDialogActive: false,
  },
  searching: false,
  selectedItemsModuleIds: [],
  selectedItemsModuleIdsIntersection: [],
  selectedMediaIds: [],
  syncingMediaIds: [],
  totalCount: 0,
};

const addFacetDialogSelectedTerm = term =>
  up({
    facetDialog: {
      selectedTerms: { $push: [term] },
    },
  });

const addModulesToMediaList = (mediaIds, moduleIds) => state => {
  const newParentModules = map(id => ({ id }), moduleIds);

  const { mediaList } = state;
  const nextMediaList = map(media => {
    if (mediaIds.includes(media.id)) {
      return up(
        {
          parentModules: {
            $set: union(media.parentModules, newParentModules),
          },
        },
        media,
      );
    } else {
      return media;
    }
  }, mediaList);

  return up(
    {
      mediaList: { $set: nextMediaList },
    },
    state,
  );
};

const addSyncingMediaIds = mediaIds =>
  up({
    syncingMediaIds: { $push: mediaIds },
  });

const applyAddModulesDrawerFilterQuery = state => {
  const { filterQuery, modules } = state.addModulesDrawer;

  const query = filterQuery.toLowerCase();

  const visibleModules = modules.filter(module => {
    const name = module.name.toLowerCase();
    const displayName = module.displayName
      ? module.displayName.toLowerCase()
      : "";
    return name.includes(query) || displayName.includes(query);
  });

  return up(
    {
      addModulesDrawer: {
        itemCount: { $set: visibleModules.length },
        itemLimit: { $set: DRAWER_ITEM_LIMIT },
        visibleModules: {
          $set: visibleModules.slice(0, DRAWER_ITEM_LIMIT),
        },
      },
    },
    state,
  );
};

const hideAddModulesDrawer = up({
  addModulesDrawer: {
    visible: { $set: false },
  },
});

const hideFacetDialog = up({
  facetDialog: {
    $set: clone(initialState.facetDialog),
  },
});

const hideRemoveModulesDrawer = up({
  removeModulesDrawer: {
    visible: { $set: false },
  },
});

const removeAppliedFacet = facetName => state => {
  const { appliedFacets } = state;

  const pipeline = [];

  if (facetName === "contentDatePublished") {
    pipeline.push(
      up({
        appliedPublicationYearGroups: { $set: [] },
      }),
    );
  }

  pipeline.push(
    up({
      appliedFacets: { $set: reject(propEq("name", facetName), appliedFacets) },
    }),
  );

  return pipe(...pipeline)(state);
};

const removeFacetDialogSelectedTerm = term => state => {
  const { selectedTerms } = state.facetDialog;

  return up(
    {
      facetDialog: {
        selectedTerms: {
          $set: reject(propEq("code", term.code), selectedTerms),
        },
      },
    },
    state,
  );
};

const removeModulesFromMediaList = (mediaIds, moduleIds) => state => {
  const { mediaList } = state;
  const nextMediaList = map(media => {
    if (mediaIds.includes(media.id)) {
      return up(
        {
          parentModules: {
            $set: (media.parentModules || []).filter(
              m => !moduleIds.includes(m.id),
            ),
          },
        },
        media,
      );
    } else {
      return media;
    }
  }, mediaList);

  return up(
    {
      mediaList: { $set: nextMediaList },
    },
    state,
  );
};

const removeQuery = up({
  appliedQuery: { $set: "" },
  query: { $set: "" },
});

const setAppliedQuery = query =>
  up({
    appliedQuery: { $set: query },
  });

const setMediaItem = (index, mediaItem) =>
  up({
    mediaList: {
      [index]: { $set: mediaItem },
    },
  });
const setLimit = limit =>
  up({
    limit: { $set: limit },
  });

const setOffset = offset =>
  up({
    offset: { $set: offset },
  });

const setSearching = searching =>
  up({
    searching: { $set: searching },
  });

const setSelectedItemsModuleIds = mediaIds => state => {
  const mediaItems = findMediaItems(state.mediaList, mediaIds);

  return up(
    {
      selectedItemsModuleIds: {
        $set: getUnionOfParentModuleIds(mediaItems),
      },
      selectedItemsModuleIdsIntersection: {
        $set: getIntersectionOfParentModuleIds(mediaItems),
      },
    },
    state,
  );
};

const setSelectedMediaIds = mediaIds =>
  up({
    selectedMediaIds: { $set: mediaIds },
  });

const showRemoveModulesDrawer = mediaItems =>
  up({
    removeModulesDrawer: {
      mediaItems: { $set: mediaItems },
      modules: { $set: [] },
      modulesLoaded: { $set: false },
      selectedModuleIds: { $set: [] },
      visible: { $set: true },
    },
  });

const showRemoveModulesConfirmationDialog = show =>
  up({
    removeModulesDrawer: {
      confirmationDialogActive: { $set: show },
    },
  });

const updateAppliedFacetsTermCounts = (
  availableFacets,
  appliedFacets,
) => state =>
  up(
    {
      appliedFacets: {
        $set: updateFacetTermCounts(availableFacets, appliedFacets),
      },
    },
    state,
  );

const updateFacetDialog = (appliedFacetsObject, availableFacets) => state => {
  const { facetDialog } = state;

  if (!isEmpty(facetDialog.name)) {
    const appliedFacet = appliedFacetsObject[facetDialog.name];
    const availableFacet = availableFacets[facetDialog.name];
    const selectedTerms = isNil(appliedFacet) ? [] : appliedFacet.terms;
    const availableTerms = isNil(availableFacet) ? [] : availableFacet.terms;
    const visible = facetDialog.visible ? !isEmpty(availableTerms) : false;

    return up(
      {
        facetDialog: {
          selectedTerms: { $set: selectedTerms },
          availableTerms: { $set: availableTerms },
          visible: { $set: visible },
        },
      },
      state,
    );
  } else {
    return state;
  }
};

const browseMediaReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.ADD_FACET_DIALOG_SELECTED_TERM:
      return addFacetDialogSelectedTerm(action.selectedTerm)(state);

    case actionTypes.ADD_FACET_DIALOG_SELECTED_PUBLICATION_YEAR: {
      const { yearTerm } = action;
      const { availablePublicationYearGroups, facetDialog } = state;
      const groupForYear = findGroupForYearTerm(
        yearTerm,
        availablePublicationYearGroups,
      );
      const selectedYearTermsInGroup = filterYearTermsInGroup(
        groupForYear,
        facetDialog.selectedTerms,
      );
      const availableYearTermsInGroup = filterYearTermsInGroup(
        groupForYear,
        facetDialog.availableTerms,
      );
      const allSelected =
        selectedYearTermsInGroup.length + 1 ===
        availableYearTermsInGroup.length;

      if (allSelected) {
        return up(
          {
            facetDialog: {
              selectedPublicationYearGroups: {
                $push: [groupForYear],
              },
              selectedTerms: {
                $set: rejectYearTermsInGroup(
                  groupForYear,
                  facetDialog.selectedTerms,
                ),
              },
            },
          },
          state,
        );
      } else {
        return addFacetDialogSelectedTerm(yearTerm)(state);
      }
    }

    case actionTypes.ADD_FACET_DIALOG_EXPANDED_PUBLICATION_YEAR_GROUP: {
      return up(
        {
          facetDialog: {
            expandedPublicationYearGroups: { $push: [action.group] },
          },
        },
        state,
      );
    }

    case actionTypes.ADD_FACET_DIALOG_SELECTED_PUBLICATION_YEAR_GROUP: {
      const { group } = action;
      const { selectedTerms } = state.facetDialog;

      return up(
        {
          facetDialog: {
            selectedPublicationYearGroups: { $push: [action.group] },
            selectedTerms: {
              $set: rejectYearTermsInGroup(group, selectedTerms),
            },
          },
        },
        state,
      );
    }

    case actionTypes.ADD_MODULES_DRAWER_MODULE_SELECTED: {
      const selectionUpdate = action.selected
        ? { $push: [action.moduleId] }
        : {
            $set: without(
              [action.moduleId],
              state.addModulesDrawer.selectedModuleIds,
            ),
          };

      return up(
        {
          addModulesDrawer: {
            selectedModuleIds: selectionUpdate,
          },
        },
        state,
      );
    }

    case actionTypes.ADD_MODULES_DRAWER_MODULES_LOADED: {
      const nextState = up(
        {
          addModulesDrawer: {
            modules: { $set: sortMedia(action.modules) },
            modulesLoaded: { $set: true },
          },
        },
        state,
      );

      return applyAddModulesDrawerFilterQuery(nextState);
    }

    case actionTypes.ADD_MODULES_DRAWER_FILTER_QUERY_CHANGED:
      return up(
        {
          addModulesDrawer: {
            filterQuery: { $set: action.filterQuery },
            selectedModuleIds: { $set: [] },
          },
        },
        state,
      );

    case actionTypes.ADD_MODULES_DRAWER_DESELECT_ALL:
      return up(
        {
          addModulesDrawer: {
            selectedModuleIds: { $set: [] },
          },
        },
        state,
      );

    case actionTypes.ADD_SELECTED_MODULES: {
      const { selectedMediaIds } = state;
      const { selectedModuleIds } = state.addModulesDrawer;

      return pipe(
        addModulesToMediaList(selectedMediaIds, selectedModuleIds),
        // setSelectedMediaIds([]),
        // setSelectedItemsModuleIds([]),
        setSelectedItemsModuleIds(selectedMediaIds),
        addSyncingMediaIds(selectedMediaIds),
        hideAddModulesDrawer,
      )(state);
    }

    case actionTypes.APPLY_ADD_MODULES_DRAWER_FILTER_QUERY:
      return applyAddModulesDrawerFilterQuery(state);

    case actionTypes.APPLY_FACET_DIALOG_FACET: {
      const {
        name,
        selectedTerms,
        selectedPublicationYearGroups,
      } = state.facetDialog;

      const pipeline = [];

      if (isEmpty(selectedTerms) && isEmpty(selectedPublicationYearGroups)) {
        pipeline.push(removeAppliedFacet(name));
      } else {
        const { appliedFacets, availableFacets } = state;
        const availableFacet = availableFacets[name];
        const appliedIndex = appliedFacets.findIndex(f => f.name === name);

        const newAppliedFacet = {
          name,
          displayName: availableFacet.displayName,
          queryParam: availableFacet.queryParam,
          terms: selectedTerms,
        };

        if (name === "contentDatePublished") {
          pipeline.push(
            up({
              appliedPublicationYearGroups: {
                $set: selectedPublicationYearGroups,
              },
            }),
          );
        }

        if (appliedIndex >= 0) {
          pipeline.push(
            up({
              appliedFacets: {
                [appliedIndex]: { $set: newAppliedFacet },
              },
            }),
          );
        } else {
          pipeline.push(
            up({
              appliedFacets: { $push: [newAppliedFacet] },
            }),
          );
        }
      }

      pipeline.push(hideFacetDialog);
      pipeline.push(setSearching(true));

      return pipe(...pipeline)(state);
    }

    case actionTypes.HIDE_ADD_MODULES_DRAWER:
      return hideAddModulesDrawer(state);

    case actionTypes.HIDE_FACET_DIALOG:
      return hideFacetDialog(state);

    case actionTypes.HIDE_REMOVE_MODULES_DRAWER:
      return hideRemoveModulesDrawer(state);

    case actionTypes.LIMIT_CHANGE:
      return pipe(
        setLimit(action.limit),
        setSearching(true),
        setSelectedMediaIds([]),
      )(state);

    case actionTypes.MEDIA_ITEMS_SYNCED:
      return up(
        {
          syncingMediaIds: {
            $set: without(action.mediaIds, state.syncingMediaIds),
          },
        },
        state,
      );

    case actionTypes.MEDIA_SELECTED: {
      const { selectedMediaIds } = state;

      const nextMediaIds = action.selected
        ? append(action.mediaId, selectedMediaIds)
        : without([action.mediaId], selectedMediaIds);

      return pipe(
        setSelectedMediaIds(nextMediaIds),
        setSelectedItemsModuleIds(nextMediaIds),
      )(state);
    }

    case applicationActionTypes.MEDIA_ITEM_UPDATED: {
      const { mediaList } = state;
      const index = findIndex(propEq("id", action.mediaItem.id))(mediaList);

      return pipe(setMediaItem(index, action.mediaItem))(state);
    }

    case actionTypes.OFFSET_CHANGE:
      return pipe(
        setOffset(action.offset),
        setSearching(true),
        setSelectedMediaIds([]),
      )(state);

    case actionTypes.QUERY_CHANGE:
      return up(
        {
          query: { $set: action.query },
        },
        state,
      );

    case actionTypes.REMOVE_FACET:
      return pipe(
        removeAppliedFacet(action.facetName),
        setSearching(true),
      )(state);

    case actionTypes.REMOVE_FACET_DIALOG_SELECTED_TERM:
      return removeFacetDialogSelectedTerm(action.selectedTerm)(state);

    case actionTypes.REMOVE_FACET_DIALOG_SELECTED_PUBLICATION_YEAR: {
      const yearTerm = action.yearTerm;
      const {
        availableTerms,
        selectedTerms,
        selectedPublicationYearGroups,
      } = state.facetDialog;

      const selectedGroupForYear = findGroupForYearTerm(
        yearTerm,
        selectedPublicationYearGroups,
      );

      if (!isNil(selectedGroupForYear)) {
        const getNewSelectedTerms = pipe(
          flip(filterYearTermsInGroup)(availableTerms),
          withoutYearTerm(yearTerm),
          unionYearTerms(selectedTerms),
        );

        return up(
          {
            facetDialog: {
              selectedPublicationYearGroups: {
                $set: withoutGroup(
                  selectedGroupForYear,
                  selectedPublicationYearGroups,
                ),
              },
              selectedTerms: {
                $set: getNewSelectedTerms(selectedGroupForYear),
              },
            },
          },
          state,
        );
      } else {
        return removeFacetDialogSelectedTerm(yearTerm)(state);
      }
    }

    case actionTypes.REMOVE_FACET_DIALOG_EXPANDED_PUBLICATION_YEAR_GROUP: {
      const { expandedPublicationYearGroups } = state.facetDialog;

      return up(
        {
          facetDialog: {
            expandedPublicationYearGroups: {
              $set: withoutGroup(action.group, expandedPublicationYearGroups),
            },
          },
        },
        state,
      );
    }

    case actionTypes.REMOVE_FACET_DIALOG_SELECTED_PUBLICATION_YEAR_GROUP: {
      const { group } = action;
      const { selectedPublicationYearGroups } = state.facetDialog;

      return up(
        {
          facetDialog: {
            selectedPublicationYearGroups: {
              $set: withoutGroup(group, selectedPublicationYearGroups),
            },
          },
        },
        state,
      );
    }

    case actionTypes.REMOVE_MODULES_DRAWER_DESELECT_ALL:
      return up(
        {
          removeModulesDrawer: {
            selectedModuleIds: { $set: [] },
          },
        },
        state,
      );

    case actionTypes.REMOVE_MODULES_DRAWER_MODULE_SELECTED: {
      const selectionUpdate = action.selected
        ? { $push: [action.moduleId] }
        : {
            $set: without(
              [action.moduleId],
              state.removeModulesDrawer.selectedModuleIds,
            ),
          };

      return up(
        {
          removeModulesDrawer: {
            selectedModuleIds: selectionUpdate,
          },
        },
        state,
      );
    }

    case actionTypes.REMOVE_MODULES_DRAWER_MODULES_LOADED:
      return up(
        {
          removeModulesDrawer: {
            modules: { $set: sortMedia(action.modules) },
            modulesLoaded: { $set: true },
          },
        },
        state,
      );

    case actionTypes.REMOVE_SELECTED_MODULES: {
      const { selectedMediaIds } = state;
      const { selectedModuleIds } = state.removeModulesDrawer;

      return pipe(
        removeModulesFromMediaList(selectedMediaIds, selectedModuleIds),
        // setSelectedMediaIds([]),
        // setSelectedItemsModuleIds([]),
        setSelectedItemsModuleIds(selectedMediaIds),
        showRemoveModulesConfirmationDialog(false),
        addSyncingMediaIds(selectedMediaIds),
        hideRemoveModulesDrawer,
      )(state);
    }

    case actionTypes.REMOVE_QUERY:
      return pipe(removeQuery, setSearching(true))(state);

    case actionTypes.SEARCH:
      return pipe(
        setAppliedQuery(state.query),
        setOffset(0),
        setSearching(true),
        setSelectedMediaIds([]),
      )(state);

    case actionTypes.SEARCH_RESULTS_LOADED: {
      const { appliedFacets, appliedPublicationYearGroups } = state;

      const facetResults = normalizeFacetResults(action.results.facets);
      const appliedFacetsObject = toObj("name", appliedFacets);
      const newAvailableFacets = mergeFacetCollections(
        appliedFacetsObject,
        facetResults,
      );

      const getPublicationGroupsForAvailableFacets = pipe(
        getPublicationYearGroups,
        unionPublicationYearGroups(appliedPublicationYearGroups),
        sortPublicationYearGroups,
      );

      return pipe(
        up({
          availableFacets: { $set: newAvailableFacets },
          availablePublicationYearGroups: {
            $set: getPublicationGroupsForAvailableFacets(newAvailableFacets),
          },
          mediaList: { $set: action.results.media },
          mediaLoaded: { $set: true },
          offset: { $set: action.results.limit.offset },
          searching: { $set: false },
          selectedMediaIds: { $set: [] },
          totalCount: { $set: action.results.totalCount },
        }),
        updateAppliedFacetsTermCounts(newAvailableFacets, appliedFacets),
        updateFacetDialog(appliedFacetsObject, newAvailableFacets),
      )(state);
    }

    case actionTypes.SELECT_ALL: {
      const nextMediaIds = action.selected
        ? map(media => media.id, state.mediaList)
        : [];

      return pipe(
        setSelectedMediaIds(nextMediaIds),
        setSelectedItemsModuleIds(nextMediaIds),
      )(state);
    }

    case actionTypes.SET_FACET_DIALOG_SELECTED_TERMS:
      return up(
        {
          facetDialog: {
            selectedTerms: { $set: action.selectedTerms },
          },
        },
        state,
      );

    case actionTypes.SHOW_ADD_MODULES_DRAWER:
      return up(
        {
          addModulesDrawer: {
            filterQuery: { $set: "" }, // leave this??
            itemCount: { $set: 0 },
            itemLimit: { $set: DRAWER_ITEM_LIMIT },
            modules: { $set: [] },
            modulesLoaded: { $set: false },
            selectedModuleIds: { $set: [] },
            visible: { $set: true },
            visibleModules: { $set: [] },
          },
        },
        state,
      );

    case actionTypes.SHOW_FACET_DIALOG: {
      const { facetName } = action;
      const {
        appliedFacets,
        availableFacets,
        appliedPublicationYearGroups,
      } = state;
      const appliedFacet = find(propEq("name", facetName), appliedFacets);
      const availableFacet = availableFacets[facetName];

      const newSelectedPublicationYearGroups =
        facetName === "contentDatePublished"
          ? clone(appliedPublicationYearGroups)
          : [];

      return up(
        {
          facetDialog: {
            selectedTerms: {
              $set: isNil(appliedFacet) ? [] : appliedFacet.terms,
            },
            selectedPublicationYearGroups: {
              $set: newSelectedPublicationYearGroups,
            },
            expandedPublicationYearGroups: { $set: [] },
            availableTerms: { $set: availableFacet.terms },
            displayName: { $set: availableFacet.displayName },
            name: { $set: availableFacet.name },
            visible: { $set: true },
          },
        },
        state,
      );
    }

    case actionTypes.SHOW_MORE_ADD_MODULES_DRAWER_MODULES: {
      const { modules, itemLimit } = state.addModulesDrawer;
      const newItemLimit = itemLimit + DRAWER_ITEM_LIMIT;

      return up(
        {
          addModulesDrawer: {
            itemLimit: {
              $set: newItemLimit,
            },
            visibleModules: {
              $set: modules.slice(0, newItemLimit),
            },
          },
        },
        state,
      );
    }

    case actionTypes.SHOW_REMOVE_MODULES_CONFIRMATION_DIALOG:
      return showRemoveModulesConfirmationDialog(action.show)(state);

    case actionTypes.SHOW_REMOVE_MODULES_DRAWER: {
      const mediaItem = action.mediaItem;

      return pipe(
        setSelectedMediaIds([mediaItem.id]),
        setSelectedItemsModuleIds([mediaItem.id]),
        showRemoveModulesDrawer([mediaItem]),
      )(state);
    }

    case actionTypes.SHOW_REMOVE_MODULES_DRAWER_FOR_SELECTIONS: {
      const selectedMediaItems = findMediaItems(
        state.mediaList,
        state.selectedMediaIds,
      );
      return showRemoveModulesDrawer(selectedMediaItems)(state);
    }

    case applicationActionTypes.ROUTE_CHANGED:
      return clone(initialState);

    default:
      return state;
  }
};

export default browseMediaReducer;
